import {
  BookOpen,
  ChevronRight,
  CodeXml,
  Eye,
  LogOut,
  Menu,
  ShieldCheck,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { ThemeSwitcher } from "./theme";

export interface WorkspaceNavItem<T extends string> {
  id: T;
  label: string;
  icon: LucideIcon;
}

export interface WorkspaceLink {
  href: string;
  label: string;
  icon: LucideIcon;
}

interface WorkspaceShellProps<T extends string> {
  variant: "account" | "developer" | "operator";
  workspaceLabel: string;
  workspaceDescription: string;
  workspaceIcon: LucideIcon;
  navLabel: string;
  navItems: readonly WorkspaceNavItem<T>[];
  activeId: T;
  onNavigate: (id: T) => void;
  account: { displayName: string; username: string };
  statusLabel: string;
  statusTone?: "good" | "warning";
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  loggingOut: boolean;
  onLogout: () => void;
  showOperator?: boolean;
  switcher?: ReactNode;
  extraLinks?: readonly WorkspaceLink[];
  children: ReactNode;
}

const globalDestinations = [
  { id: "account", href: "/account", label: "账号中心", icon: UserRound },
  { id: "developer", href: "/developer", label: "开发者中心", icon: CodeXml },
  { id: "operator", href: "/operator", label: "运营控制台", icon: ShieldCheck },
] as const;

export function WorkspaceShell<T extends string>({
  variant,
  workspaceLabel,
  workspaceDescription,
  workspaceIcon: WorkspaceIcon,
  navLabel,
  navItems,
  activeId,
  onNavigate,
  account,
  statusLabel,
  statusTone = "good",
  menuOpen,
  setMenuOpen,
  loggingOut,
  onLogout,
  showOperator = false,
  switcher,
  extraLinks = [],
  children,
}: WorkspaceShellProps<T>) {
  const activeItem = navItems.find((item) => item.id === activeId);
  const destinations = globalDestinations.filter((item) => item.id !== "operator" || showOperator || variant === "operator");

  return (
    <div className={`workspace-shell workspace-${variant}`}>
      <aside className={`workspace-drawer${menuOpen ? " open" : ""}`} aria-label="应用导航">
        <div className="workspace-rail">
          <a className="rail-brand" href="/account" title="EvilEyeAuth" aria-label="EvilEyeAuth 账号中心">
            <Eye aria-hidden="true" />
          </a>
          <nav aria-label="工作区切换">
            {destinations.map(({ id, href, label, icon: Icon }) => (
              <a className={variant === id ? "active" : ""} href={href} key={id} title={label} aria-label={label} aria-current={variant === id ? "page" : undefined}>
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </a>
            ))}
          </nav>
          <a className="rail-docs" href="/docs/" target="_blank" rel="noreferrer" title="接入文档" aria-label="接入文档">
            <BookOpen aria-hidden="true" />
          </a>
          <span className={`rail-assurance ${statusTone}`} title={statusLabel} aria-hidden="true" />
        </div>

        <div className="workspace-nav-pane">
          <header className="workspace-product">
            <span className="workspace-product-icon"><WorkspaceIcon aria-hidden="true" /></span>
            <div><small>EvilEyeAuth</small><strong>{workspaceLabel}</strong></div>
            <button className="drawer-close" type="button" aria-label="关闭导航" onClick={() => setMenuOpen(false)}><X aria-hidden="true" /></button>
          </header>

          <p className="workspace-description">{workspaceDescription}</p>
          {switcher}

          <div className="workspace-nav-label">{navLabel}</div>
          <nav className="workspace-page-nav" aria-label={workspaceLabel}>
            {navItems.map(({ id, label, icon: Icon }) => (
              <button
                className={activeId === id ? "active" : ""}
                type="button"
                key={id}
                aria-current={activeId === id ? "page" : undefined}
                onClick={() => onNavigate(id)}
              >
                <Icon aria-hidden="true" />
                <span>{label}</span>
                <ChevronRight className="nav-chevron" aria-hidden="true" />
              </button>
            ))}
          </nav>

          {extraLinks.length > 0 && (
            <nav className="workspace-extra-links" aria-label="相关入口">
              {extraLinks.map(({ href, label, icon: Icon }) => <a href={href} key={href}><Icon aria-hidden="true" /><span>{label}</span></a>)}
            </nav>
          )}

          <footer className="workspace-nav-footer">
            <div className={`workspace-session ${statusTone}`}><span /><div><strong>受保护的当前会话</strong><small>{statusTone === "good" ? "身份验证有效" : "需要安全验证"}</small></div></div>
            <div className="workspace-user">
              <span className="workspace-avatar">{account.displayName.slice(0, 1).toUpperCase()}</span>
              <div><strong>{account.displayName}</strong><span>@{account.username}</span></div>
              <button type="button" title="退出登录" aria-label="退出登录" disabled={loggingOut} onClick={onLogout}><LogOut aria-hidden="true" /></button>
            </div>
          </footer>
        </div>
      </aside>

      {menuOpen && <button className="workspace-scrim" type="button" aria-label="点击遮罩关闭导航" onClick={() => setMenuOpen(false)} />}

      <main className="workspace-main">
        <header className="workspace-topbar">
          <button className="workspace-menu" type="button" aria-label="打开导航" onClick={() => setMenuOpen(true)}><Menu aria-hidden="true" /></button>
          <div className="workspace-breadcrumb"><span>{workspaceLabel}</span><ChevronRight aria-hidden="true" /><strong>{activeItem?.label ?? workspaceLabel}</strong></div>
          <div className="workspace-topbar-actions">
            <ThemeSwitcher />
            <div className={`topbar-assurance ${statusTone}`} role="status" aria-label={statusLabel}><span /><strong>{statusLabel}</strong></div>
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}
